import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, Eye } from "lucide-react";
import { api, LEAD_STATUSES } from "../lib/api";

const STATUS_COLORS = {
  New: "bg-blue-100 text-blue-700", Contacted: "bg-amber-100 text-amber-700",
  "Estimate Scheduled": "bg-purple-100 text-purple-700", "Estimate Sent": "bg-cyan-100 text-cyan-700",
  Won: "bg-green-100 text-green-700", Lost: "bg-red-100 text-red-700", Closed: "bg-slate-200 text-slate-600",
};

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [status, setStatus] = useState("All");
  const [q, setQ] = useState("");
  const [archived, setArchived] = useState(false);

  const load = useCallback(() => {
    api.get("/leads", { params: { status, q: q || undefined, archived } }).then((r) => setLeads(r.data)).catch(() => {});
  }, [status, q, archived]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div data-testid="portal-leads-page">
      <h1 className="font-heading font-extrabold text-[#0B1B3D] text-2xl md:text-3xl mb-8">Estimate Requests</h1>
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className="field !h-11 !pl-9" placeholder="Search name, phone, service…" data-testid="leads-search-input"
            value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <select className="field !h-11 w-48" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="leads-status-filter">
          {["All", ...LEAD_STATUSES].map((s) => <option key={s}>{s}</option>)}
        </select>
        <button onClick={() => setArchived(!archived)} data-testid="leads-archived-toggle"
          className={`px-4 h-11 font-heading font-semibold text-xs uppercase tracking-widest border ${archived ? "bg-[#0B1B3D] text-white border-[#0B1B3D]" : "bg-white text-slate-500 border-slate-300"}`}>
          {archived ? "Showing Archived" : "Show Archived"}
        </button>
      </div>
      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left">
              {["Lead ID", "Date", "Customer", "Phone", "Email", "Service", "City / ZIP", "Preferred Date", "Preferred Time", "Status", ""].map((h) => (
                <th key={h} className="px-4 py-3 font-heading font-bold text-xs uppercase tracking-widest text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`lead-row-${l.id}`}>
                <td className="px-4 py-3 font-mono text-xs text-slate-500">#{l.id.slice(0, 8).toUpperCase()}</td>
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{new Date(l.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 font-heading font-bold text-[#0B1B3D]">{l.name}</td>
                <td className="px-4 py-3"><a href={`tel:${l.phone}`} className="text-[#005AE0]">{l.phone}</a></td>
                <td className="px-4 py-3 text-slate-600">{l.email || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{l.service}</td>
                <td className="px-4 py-3 text-slate-600">{[l.city, l.zip_code].filter(Boolean).join(", ") || "—"}</td>
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{l.preferred_date || "—"}</td>
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{l.preferred_time || "—"}</td>
                <td className="px-4 py-3"><span className={`text-xs font-heading font-bold px-2.5 py-1 whitespace-nowrap ${STATUS_COLORS[l.status] || "bg-slate-100"}`}>{l.status}</span></td>
                <td className="px-4 py-3">
                  <Link to={`/portal/leads/${l.id}`} data-testid={`lead-view-${l.id}`} className="inline-flex items-center gap-1 text-[#005AE0] font-semibold"><Eye className="w-4 h-4" /> View</Link>
                </td>
              </tr>
            ))}
            {leads.length === 0 && <tr><td colSpan={11} className="px-4 py-12 text-center text-slate-400">No leads found.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
